import { countryData } from "./countries";

/** What a postcode is called, by Google's `zip_name_type`, in a label's
 *  words ("Billing postcode" is "Billing ZIP code" in the US). */
const POSTCODE_NOUNS: Readonly<Record<string, string>> = {
  postal: "postcode",
  zip: "ZIP code",
  eircode: "Eircode",
  pin: "PIN code",
};
/** What a region is called, by Google's `state_name_type`. */
export const REGION_LABELS: Readonly<Record<string, string>> = {
  area: "Area",
  county: "County",
  department: "Department",
  district: "District",
  do_si: "Province",
  emirate: "Emirate",
  island: "Island",
  oblast: "Region",
  parish: "Parish",
  prefecture: "Prefecture",
  province: "Province",
  state: "State",
};
/** What a town is called where it is not a town or city, by Google's
 *  `locality_name_type`. */
export const CITY_LABELS: Readonly<Record<string, string>> = {
  district: "District",
  suburb: "Suburb",
};

/** What a postcode is called in the country ("postcode", "ZIP code",
 *  "Eircode"). */
export const postcodeNoun = (country: string | null | undefined) =>
  POSTCODE_NOUNS[countryData(country)?.postcode?.kind ?? "postal"] ?? "postcode";

/** The postcode's noun in a label: "Postcode" is "ZIP code", "Billing
 *  postcode" is "Billing ZIP code". */
export const withPostcodeNoun = (label: string, noun: string) =>
  label.replace(/postcode/i, (word) =>
    word[0] === "P" ? `${noun[0]!.toUpperCase()}${noun.slice(1)}` : noun,
  );

/** What the country's region is called ("State", "County"), where its
 *  addresses have one; "Region" for a kind with no name of its own. */
export const regionLabel = (kind: string) => REGION_LABELS[kind] ?? "Region";

/** What the country's town is called where it is not a town or city
 *  ("Suburb"); none where it is. */
export const cityLabel = (country: string | null | undefined): string | undefined => {
  const kind = countryData(country)?.city;
  return kind ? CITY_LABELS[kind] : undefined;
};

/**
 * A postcode as typed, read in its country: trimmed, its spaces single and
 * its letters capitals ("sw1a  2aa" is "SW1A 2AA"), and null where the
 * country's postcodes have a pattern it does not fit. Any postcode fits a
 * country whose postcodes have none, or one the file does not know. The
 * identity's own bound on a postcode (`buyerSchema`) is checked by its
 * callers.
 */
export function readPostcode(postcode: string, country: string | null | undefined): string | null {
  const read = postcode.trim().replace(/\s+/g, " ").toUpperCase();
  const pattern = countryData(country)?.postcode?.pattern;
  if (pattern && !new RegExp(`^(?:${pattern})$`, "i").test(read)) return null;
  return read;
}

/** The UK's four nations (England, Northern Ireland, Scotland, Wales) by
 *  their ISO 3166-2:GB codes, the form a region is saved in ("GB-SCT", as
 *  "US-CA"). */
export type UkNation = "GB-ENG" | "GB-NIR" | "GB-SCT" | "GB-WLS";

// Which nation each UK postcode is in, from the ONS's National Statistics
// Postcode Lookup, August 2026 (NSPL_AUG_2026_UK,
// https://www.arcgis.com/home/item.html?id=9d8364ebae8b4439aa66cda440e54fc8,
// Open Government Licence): the country of every live postcode (no
// `doterm`), placed by its grid reference (`ctry26cd`), read on 23 Oct 2026.
// A postcode area whose live postcodes all lie in one nation is that
// nation's, whatever its district. An area that crosses a border is read by
// district: a district whose live postcodes all lie in one nation is that
// nation's, and one whose live postcodes lie in two is ambiguous, as is a
// district the lookup places nowhere (CH90, with no grid reference) or does
// not list. Postcodes with no grid reference are left out, as they place
// nothing. The Channel Islands (GY, JE) and the Isle of Man (IM) are in none
// of the four nations; nor are BFPO, the non-geographic BF and BX, or
// Girobank's GIR 0AA.

/** Areas wholly in one nation. */
const WHOLE_AREAS: Readonly<Record<UkNation, string>> = {
  "GB-ENG":
    "AL B BA BB BD BH BL BN BR BS CA CB CM CO CR CT CV CW DA DE DH DL DN DT DY E EC EN EX FY GU HA HD HG HP HU HX IG IP KT L LA LE LN LS LU M ME MK N NE NG NN NR NW OL OX PE PL PO PR RG RH RM S SE SG SK SL SM SN SO SP SR SS ST SW TA TF TN TQ TR TS TW UB W WA WC WD WF WN WR WS WV YO",
  "GB-NIR": "BT",
  "GB-SCT": "AB DD EH FK G HS IV KA KW KY ML PA PH ZE",
  "GB-WLS": "CF SA",
};

/** Areas that cross a border, by their districts' numbers: each nation's,
 *  and the ambiguous ones, whose live postcodes lie in two. */
const BORDER_AREAS: Readonly<Record<string, Partial<Record<UkNation | "ambiguous", string>>>> = {
  CH: { "GB-ENG": "2-3 25-34 41-49 60-66 70 88 99", "GB-WLS": "5-8", ambiguous: "1 4" },
  DG: { "GB-SCT": "1-14", ambiguous: "16" },
  GL: { "GB-ENG": "1-15 17-20 50-56", ambiguous: "16" },
  HR: { "GB-ENG": "1-2 4 6-9", ambiguous: "3 5" },
  LD: { "GB-WLS": "1-6", ambiguous: "7-8" },
  LL: { "GB-WLS": "11-13 15-49 51-78", ambiguous: "14" },
  NP: { "GB-WLS": "4 8 10-13 15 18-20 22-24 26 44", ambiguous: "7 16 25" },
  SY: { "GB-ENG": "1-4 6 8 11-12 99", "GB-WLS": "16-20 23-25", ambiguous: "5 7 9-10 13-15 21-22" },
  TD: { "GB-SCT": "1-4 6-8 10-11 13-14", ambiguous: "5 9 12 15" },
};

type NationTable = { areas: Map<string, UkNation>; districts: Map<string, UkNation | "ambiguous"> };
let nationTable: NationTable | undefined;
// Built when first asked, not at import.
function nations(): NationTable {
  if (nationTable) return nationTable;
  const areas = new Map<string, UkNation>();
  for (const [nation, list] of Object.entries(WHOLE_AREAS) as [UkNation, string][])
    for (const area of list.split(" ")) areas.set(area, nation);
  const districts = new Map<string, UkNation | "ambiguous">();
  for (const [area, groups] of Object.entries(BORDER_AREAS))
    for (const [verdict, list] of Object.entries(groups) as [UkNation | "ambiguous", string][])
      for (const range of list.split(" ")) {
        const [from, to = from] = range.split("-").map(Number) as [number, number?];
        for (let district = from; district <= to; district++) districts.set(`${area}${district}`, verdict);
      }
  nationTable = { areas, districts };
  return nationTable;
}

/**
 * The UK nation a postcode is in ("GB-SCT"), read as a UK postcode
 * (`readPostcode`): "ambiguous" where its district crosses a border (parts
 * of CH, DG, GL, HR, LD, LL, NP, SY and TD), so only the buyer can say; null
 * where it is no UK postcode, or one in none of the four nations (Jersey's,
 * BFPO's). Pure.
 */
export function ukNationOf(postcode: string): UkNation | "ambiguous" | null {
  const read = readPostcode(postcode, "GB");
  if (read === null) return null;
  // The outward code is all but the last three characters ("SW1A 2AA").
  const outward = read.replace(/ /g, "").slice(0, -3);
  const area = /^[A-Z]+/.exec(outward)?.[0] ?? "";
  const { areas, districts } = nations();
  const whole = areas.get(area);
  if (whole) return whole;
  if (BORDER_AREAS[area] === undefined) return null;
  return districts.get(outward) ?? "ambiguous";
}

/** The region of a list typed by its value ("US-CA"), its code alone
 *  ("CA") or its name ("California"), in any case: its value, or null
 *  where it is none of the list's. */
export function matchRegion(
  options: readonly { value: string; label: string }[],
  typed: string,
): string | null {
  const key = typed.trim().toLowerCase();
  const option = options.find((choice) =>
    [choice.value, choice.value.slice(choice.value.indexOf("-") + 1), choice.label].some(
      (name) => name.toLowerCase() === key,
    ),
  );
  return option ? option.value : null;
}
